import { GET_ONBOARDING_V2_VARIANT } from '@/components/constants/flags';
import { useManualExposure } from '@/hooks/amplitude/use-manual-exposure';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import type { SignupPhoneFieldSurface } from '@/models/analytics/features/onboarding';
import { getAllottedCountryConfigKey } from '@/utils/country-config';
import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';

interface UseTrackSignupPhoneFieldViewedParams {
  isVisible: boolean;
  isOptional: boolean;
  surface: SignupPhoneFieldSurface;
  countryCode?: string;
}

/** Fires `signup_phone_field_viewed` once when the field first becomes visible. */
export function useTrackSignupPhoneFieldViewed({
  isVisible,
  isOptional,
  surface,
  countryCode,
}: UseTrackSignupPhoneFieldViewedParams): void {
  const hasTracked = useRef(false);
  const usPhoneFlagRawVariant =
    countryCode === 'US'
      ? posthog.getFeatureFlag('release-phone-number-in-signup-us')
      : null;
  const usPhoneFlagVariant =
    usPhoneFlagRawVariant === 'optional' || usPhoneFlagRawVariant === 'required'
      ? usPhoneFlagRawVariant
      : 'hidden';
  const isUsPhoneFlagParticipant = usPhoneFlagVariant !== 'hidden';

  useManualExposure({
    flagKey: 'release-phone-number-in-signup-us',
    // variant override is authoritative; isEnabled remains for readability.
    isEnabled: isUsPhoneFlagParticipant,
    isVisible: isVisible && isUsPhoneFlagParticipant,
    variant: usPhoneFlagVariant,
  });

  useEffect(() => {
    if (!isVisible || hasTracked.current) return;
    hasTracked.current = true;
    track({
      event_name: TrackingEventName.SignupPhoneFieldViewed,
      props: {
        phone_field_mode: isOptional ? 'optional' : 'required',
        country_code: countryCode,
        country_config: getAllottedCountryConfigKey(countryCode),
        surface,
        // onboarding_variant only carries meaning for the onboarding-v2 step
        // surfaces. The signup modal and the legacy organisation survey predate
        // the onboarding-v2 experiment, so attaching a variant there is misleading.
        ...(surface === 'onboarding_role_step' || surface === 'onboarding_about_you_step'
          ? { onboarding_variant: GET_ONBOARDING_V2_VARIANT() }
          : {}),
      },
      options: { posthog: true, amplitude: true },
    });
  }, [isVisible, isOptional, surface, countryCode]);
}
